/**
 * Stripe Service - Frontend API calls to Stripe backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

/**
 * Create Stripe Checkout Session
 * Uses simplified endpoint: /api/checkout/create-session
 */
export async function createCheckoutSession(
  planId: 'plus' | 'pro' | 'elite',
  userId?: string
): Promise<CheckoutSessionResponse> {
  try {
    // Try serverless function endpoint first (same origin)
    const serverlessUrl = typeof window !== 'undefined' 
      ? `${window.location.origin}/api/checkout/create-session`
      : `${API_BASE_URL}/api/checkout/create-session`;
    
    // Fallback to explicit API URL if configured
    const endpoint = process.env.REACT_APP_API_URL 
      ? `${process.env.REACT_APP_API_URL}/api/checkout/create-session`
      : serverlessUrl;

    console.log('Calling checkout endpoint:', endpoint);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ plan: planId }), // Use 'plan' as specified in requirements
    });

    // Check if response is JSON or HTML (error page)
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    if (!response.ok) {
      let errorData;
      try {
        if (isJson) {
          errorData = await response.json();
        } else {
          // Got HTML instead of JSON - backend probably not deployed
          const htmlText = await response.text();
          console.error('Backend returned HTML instead of JSON. Response:', htmlText.substring(0, 200));
          errorData = { 
            error: 'Backend server not configured. Please deploy the backend server and set REACT_APP_API_URL environment variable.' 
          };
        }
      } catch (parseError) {
        errorData = { error: 'Network error or backend not available' };
      }
      
      const errorMessage = errorData.error || `HTTP ${response.status}: Failed to create checkout session`;
      console.error('Checkout API error:', errorMessage, response.status, endpoint);
      throw new Error(errorMessage);
    }

    // Check if we got HTML instead of JSON
    if (!isJson) {
      const htmlText = await response.text();
      console.error('Backend returned HTML instead of JSON. Response:', htmlText.substring(0, 200));
      throw new Error('Backend server not configured. Please deploy the backend server and set REACT_APP_API_URL environment variable.');
    }

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('Failed to parse JSON response:', parseError);
      throw new Error('Invalid response from server. Backend may not be properly configured.');
    }
    
    if (!data.url) {
      throw new Error('No checkout URL received from server');
    }
    
    return { url: data.url, sessionId: data.sessionId || '' };
  } catch (error: any) {
    // Handle network errors
    if (error.message.includes('fetch') || 
        error.message.includes('Network') || 
        error.message.includes('Failed to fetch') ||
        error.name === 'TypeError') {
      console.error('Network error calling checkout API:', error);
      throw new Error('Unable to connect to payment service. Please check your connection and try again.');
    }
    throw error;
  }
}

/**
 * Create Stripe Customer Portal Session
 */
export async function createPortalSession(userId: string): Promise<PortalSessionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/stripe/create-portal-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create portal session');
  }

  return response.json();
}

